export type HttpClientOptions = {
  baseUrl: string;
  getAccessToken: () => string | null;
  setAccessToken: (token: string | null) => void;
  refresh: () => Promise<string | null>;
  onUnauthorized?: () => void;
};

type RequestOptions = Omit<RequestInit, "method"> & {
  headers?: Record<string, string>;
};

export const createHttpClient = (options: HttpClientOptions) => {
  if (!options.baseUrl || options.baseUrl.trim().length === 0) {
    throw new Error("HTTP client baseUrl is required.");
  }
  const request = async (
    method: string,
    path: string,
    body?: unknown,
    requestOptions: RequestOptions = {},
    retry = false
  ): Promise<Response> => {
    const headers = new Headers(requestOptions.headers);
    const token = options.getAccessToken();

    if (!headers.has("Accept")) {
      headers.set("Accept", "application/json");
    }

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (body !== undefined) {
      headers.set("Content-Type", "application/json");
    }

    const response = await fetch(`${options.baseUrl}${path}`, {
      ...requestOptions,
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const contentType = response.headers.get("content-type") || "";
    if (response.ok && !contentType.includes("application/json")) {
      const bodyText = await response.clone().text().catch(() => "");
      const errorPayload = {
        error: "Resposta inesperada do servidor.",
        status: response.status,
        body: bodyText.slice(0, 300),
      };
      return new Response(JSON.stringify(errorPayload), {
        status: 502,
        headers: { "Content-Type": "application/json" },
      });
    }

    if (response.status === 401 && !retry) {
      const newToken = await options.refresh();
      if (newToken) {
        options.setAccessToken(newToken);
        return request(method, path, body, requestOptions, true);
      }
      options.onUnauthorized?.();
    }

    if (!response.ok) {
      const bodyText = await response.clone().text().catch(() => "");
      const error = {
        url: `${options.baseUrl}${path}`,
        status: response.status,
        body: bodyText,
      };
      (response as Response & { error?: typeof error }).error = error;
      console.error("HTTP request failed", error);
    }

    return response;
  };

  return {
    get: (path: string, options?: RequestOptions) =>
      request("GET", path, undefined, options),
    post: (path: string, body?: unknown, options?: RequestOptions) =>
      request("POST", path, body, options),
    put: (path: string, body?: unknown, options?: RequestOptions) =>
      request("PUT", path, body, options),
    del: (path: string, options?: RequestOptions) =>
      request("DELETE", path, undefined, options),
  };
};
